import {createFileRoute} from "@tanstack/react-router";
import {useTranslation} from "react-i18next";
import {
    allRewindCheckpointsTqo,
    allRewindCheckpointsVerb,
    rewindToCheckpointVerb,
    SpawnCheckpointRequest,
    spawnCheckpointVerb,
} from "../../../rpc/webrpc-client.gen.ts";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {Alert, Button, Divider, FormControl, FormLabel, Input, Sheet, Stack, Typography} from "@mui/joy";
import Grid from "@mui/material/Grid";
import {TaskProgressReport} from "../../../Task.tsx";
import {format} from "date-fns";
import {useForm} from "react-hook-form";
import {Fragment} from "react";
import {useInfallibleQuery} from "../../../util/useInfallibleQuery.tsx";
import {PageBody, PageHeader, PageShell} from "../../../routing/shell.tsx";
import {TopBar} from "../../../components/TopBar.tsx";
import {useMothership} from "../../../tq/tqx.tsx";

interface Inputs {
    checkpointName: string;
}

function Rewind() {
    const {t} = useTranslation();
    const tqc = useQueryClient();
    const mama = useMothership();
    //const resetDispatchMesh = useResetDispatchMesh();

    const checkpointsTqrv = useInfallibleQuery(allRewindCheckpointsTqo(mama));

    const {
        mutate: doRewind,
        error: rewindError,
        isPending: rewindPending,
        isSuccess: rewindCompleted,
    } = useMutation({
        mutationKey: [rewindToCheckpointVerb],
        mutationFn: (checkpointId: string) => mama.rewindToCheckpoint({checkpointId}),
        onSuccess: () => {
            tqc.clear();
            //resetDispatchMesh();
        },
    });

    const {register, handleSubmit, reset}
        = useForm<Inputs>({
        defaultValues: {
            checkpointName: "",
        }
    });

    const {mutate: spawnCheckpoint}
        = useMutation({
        mutationKey: [spawnCheckpointVerb],
        mutationFn: (args: SpawnCheckpointRequest) => {
            return mama.spawnCheckpoint(args);
        },
        onSuccess: () => {
            reset();
            void tqc.invalidateQueries({queryKey: [allRewindCheckpointsVerb]})
        }
    });

    const onSubmitSpawn = handleSubmit(data => {
        spawnCheckpoint({name: data.checkpointName});
    });

    if (checkpointsTqrv.isPending) {
        return <TaskProgressReport action={t("admin.fetchingRewindCheckpoints")}/>
    }

    if (rewindError) {
        return <Alert variant="solid" color="danger">{JSON.stringify(rewindError)}</Alert>
    }

    if (rewindPending) {
        return <TaskProgressReport action={t("admin.rewinding")}/>
    }

    if (rewindCompleted) {
        return <Alert color="success">{t("admin.rewindComplete")}</Alert>
    }

    const fetchedCheckpoints = checkpointsTqrv.data;

    return <PageShell>
        <PageHeader><TopBar/></PageHeader>
        <PageBody>
            <Sheet>
                <Grid container spacing={1} sx={{flexGrow: 1, alignItems: "center"}}>
                    <Grid size={{xs: 5}}>
                        <Typography level="body-md" sx={{textAlign: "center"}}>
                            {t("admin.checkpointGrid.headers.name")}
                        </Typography>
                    </Grid>
                    <Grid size={{xs: 3}}>
                        <Typography level="body-md" sx={{textAlign: "center"}}>
                            {t("admin.checkpointGrid.headers.asof")}
                        </Typography>
                    </Grid>
                    <Grid size={{xs: 2}}>
                        <Typography level="body-md" sx={{textAlign: "center"}}>
                            {t("admin.checkpointGrid.headers.author")}
                        </Typography>
                    </Grid>
                    <Grid size={{xs: 2}}>
                        <Typography level="body-md"></Typography>
                    </Grid>
                    {fetchedCheckpoints.map(cp => {
                        return <Fragment key={cp.name}>
                            <Grid size={{xs: 5}}>
                                <Typography level="body-sm">{cp.name}</Typography>
                            </Grid>
                            <Grid size={{xs: 3}}>
                                <Typography level="body-sm">{format(cp.asof, "yyyy.MM.dd hh:mm:ss")}</Typography>
                            </Grid>
                            <Grid size={{xs: 2}}>
                                <Typography level="body-sm">{cp.author.displayName}</Typography>
                            </Grid>
                            <Grid size={{xs: 2}}>
                                <Button variant="solid" color="danger" onClick={() => {
                                    doRewind(cp.id)
                                }}>
                                    rewind
                                </Button>
                            </Grid>
                        </Fragment>
                    })}
                </Grid>

                <Divider sx={{mt: "0.6rem", mb: "1.8rem", bgcolor: "primary.700", height: "0.4rem"}}/>

                <Sheet>
                    {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
                    <form onSubmit={onSubmitSpawn}>
                        <Stack direction="column" spacing={2}>
                            <Stack direction="row" sx={{justifyContent: "center"}}>
                                <Typography level="title-lg">{t("admin.newCheckpointLabel")}</Typography>
                            </Stack>
                            <FormControl>
                                <FormLabel>{t("admin.checkpointGrid.headers.name")}</FormLabel>
                                <Input {...register("checkpointName", {required: true})}/>
                            </FormControl>
                            <Button type="submit" color="primary" variant="solid" size="lg"
                                    sx={{px: "4rem", py: "1rem"}}>
                                {t("admin.makeCheckpoint")}
                            </Button>
                        </Stack>
                    </form>
                </Sheet>
            </Sheet>
        </PageBody>
    </PageShell>;
}

export const Route = createFileRoute("/_auth/admin/rewind")({
    component: Rewind,
});

